import { Button, Spinner, Form, Modal } from 'react-bootstrap';
import { IconCheck } from '@tabler/icons-react';
import Select from '../../../components/form/AppSelect';
import type { ThemeConfig } from 'react-select';
import { useTranslation } from 'react-i18next';

interface TariffTypeOption {
  id: number | string;
  name: string;
}

interface TariffFormData {
  name: string;
  tariff_type: number | string | null;
  amount: string;
  amount_vat: string;
  valid_from: string;
  valid_to: string;
  priority: number | string;
  is_active: boolean;
  is_promotional: boolean;
  note: string;
  description: string;
}

interface TariffFormModalProps {
  show: boolean;
  editing: boolean;
  formData: TariffFormData;
  tariffTypes: TariffTypeOption[];
  submitting: boolean;
  vatRate?: number | null;
  selectTheme: ThemeConfig;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onChange: (field: keyof TariffFormData, value: string | number | boolean | null) => void;
}

const TariffFormModal = ({
  show,
  editing,
  formData,
  tariffTypes,
  submitting,
  vatRate,
  selectTheme,
  onClose,
  onSubmit,
  onChange,
}: TariffFormModalProps) => {
  const { t } = useTranslation();

  const handleAmountChange = (amount: string) => {
    onChange('amount', amount);
    if (vatRate === null || vatRate === undefined) return;

    const netAmount = Number(amount);
    onChange('amount_vat', amount !== '' && Number.isFinite(netAmount)
      ? (netAmount * (1 + vatRate / 100)).toFixed(2)
      : '');
  };

  const handleAmountVatChange = (amountVat: string) => {
    onChange('amount_vat', amountVat);
    if (vatRate === null || vatRate === undefined || vatRate === 0) return;

    const grossAmount = Number(amountVat);
    onChange('amount', amountVat !== '' && Number.isFinite(grossAmount)
      ? (grossAmount / (1 + vatRate / 100)).toFixed(2)
      : '');
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editing ? (t('warehouses.tariffs.editTariff') || 'Edit Tariff') : (t('warehouses.tariffs.addTariff') || 'Add Tariff')}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('warehouses.tariffs.name') || 'Name'} *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(event) => onChange('name', event.target.value)}
              placeholder={t('warehouses.tariffs.namePlaceholder') || 'e.g., Summer Special, Weekend Rate'}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('warehouses.tariffs.type') || 'Type'} *</Form.Label>
            <Select
              value={
                formData.tariff_type
                  ? tariffTypes
                      .filter((type) => type.id === formData.tariff_type)
                      .map((type) => ({ value: type.id, label: type.name }))[0] || null
                  : null
              }
              onChange={(option) => onChange('tariff_type', option ? option.value : null)}
              options={tariffTypes.map((type) => ({ value: type.id, label: type.name }))}
              theme={selectTheme}
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              placeholder={t('warehouses.tariffs.selectType') || 'Select tariff type...'}
              required
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t('warehouses.tariffs.amount') || 'Amount'} *</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={formData.amount}
                  onChange={(event) => handleAmountChange(event.target.value)}
                  placeholder="0.00"
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t('warehouses.tariffs.amountVat') || 'Amount with VAT'}</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={formData.amount_vat}
                  onChange={(event) => handleAmountVatChange(event.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t('warehouses.tariffs.validFrom') || 'Valid From'}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.valid_from}
                  onChange={(event) => onChange('valid_from', event.target.value)}
                />
                <Form.Text className="text-muted">
                  {t('warehouses.tariffs.validFromHelp') || 'Leave empty for no start date'}
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t('warehouses.tariffs.validTo') || 'Valid To'}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.valid_to}
                  onChange={(event) => onChange('valid_to', event.target.value)}
                />
                <Form.Text className="text-muted">
                  {t('warehouses.tariffs.validToHelp') || 'Leave empty for no end date'}
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>{t('warehouses.tariffs.priority') || 'Priority'}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={formData.priority}
              onChange={(event) => onChange('priority', event.target.value)}
            />
            <Form.Text className="text-muted">
              {t('warehouses.tariffs.priorityHelp') || 'Higher priority tariffs are used first'}
            </Form.Text>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={t('warehouses.tariffs.isActive') || 'Active'}
                  checked={formData.is_active}
                  onChange={(event) => onChange('is_active', event.target.checked)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={t('warehouses.tariffs.isPromotional') || 'Promotional'}
                  checked={formData.is_promotional}
                  onChange={(event) => onChange('is_promotional', event.target.checked)}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>{t('warehouses.tariffs.description') || 'Description'}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(event) => onChange('description', event.target.value)}
              placeholder={t('warehouses.tariffs.descriptionPlaceholder') || 'Brief description of this tariff...'}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('warehouses.tariffs.note') || 'Internal Note'}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.note}
              onChange={(event) => onChange('note', event.target.value)}
              placeholder={t('warehouses.tariffs.notePlaceholder') || 'Internal notes (not visible to customers)...'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <IconCheck size={16} className="me-1" />
                {t('common.save')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TariffFormModal;
